import { useState, useCallback, useEffect, useRef } from 'react';
import { Photo } from '../api/types';
import { PhotoStreamClient } from '../api/PhotoStreamClient';

interface UsePhotosReturn {
  photos: Photo[];
  loading: boolean;
  error: string | null;
  loadPhotos: (galleryId: string) => Promise<void>;
  refreshPhotos: () => Promise<void>;
}

export const usePhotos = (apiClient: PhotoStreamClient): UsePhotosReturn => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentGalleryId, setCurrentGalleryId] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const loadPhotos = useCallback(async (galleryId: string) => {
    try {
      setLoading(true);
      setError(null);
      setCurrentGalleryId(galleryId);
      const photoList = await apiClient.listPhotos(galleryId);
      setPhotos(photoList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  const silentRefresh = useCallback(async () => {
    if (!currentGalleryId) return;
    
    try {
      const photoList = await apiClient.listPhotos(currentGalleryId);
      setPhotos(photoList);
      setError(null);
    } catch (err) {
      // Silent refresh - don't show errors to avoid disrupting UX
      console.warn('Silent photo refresh failed:', err);
    }
  }, [currentGalleryId, apiClient]);

  const refreshPhotos = useCallback(async () => {
    if (currentGalleryId) {
      await loadPhotos(currentGalleryId);
    }
  }, [currentGalleryId, loadPhotos]);

  // Auto-refresh every 10 seconds (silent)
  useEffect(() => {
    if (currentGalleryId) {
      intervalRef.current = setInterval(() => {
        silentRefresh();
      }, 2000); // 2 seconds

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [currentGalleryId, silentRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    photos,
    loading,
    error,
    loadPhotos,
    refreshPhotos,
  };
};
